import React from 'react';
import PropTypes from 'prop-types';
import {
  useHelmet, TitleTag, MetaTag, LinkTag, ScriptTag
} from '@thd-nucleus/thd-helmet';
import {
  params,
  arrayOf,
  shape,
  string,
  number,
  json,
  useDataModel
} from '@thd-nucleus/data-sources';

import { getStoreDetailStructureData } from './structured-data';
import { getStructuredBreadcrumbs } from '../helpers/clean-breadcrumb-data';
import { getGraphQlReviewsOptions, getStoreDetails } from '../helpers/pps-helpers';
import {
  EMT_PAGE_NAMES,
  PAGE_TYPE,
  LAST_PATH,
  REVIEWS_PAGE_SIZE,
  OPEN_GRAPH_FACEBOOK_ADMIN,
  SD_OG_IMAGE
} from '../constants';

const getStoreReviewsTitle = ({
  city,
  storeName = '',
  postalCode,
  seoPageNumber,
  state
}) => {
  const hasRequiredPostfixData = !!city && !!state && !!postalCode;
  const titleSuffix = hasRequiredPostfixData ? `| ${city}, ${state} ${postalCode}` : '';

  return `${storeName} Home Depot Store Reviews, Pg ${seoPageNumber} ${titleSuffix}`;
};

const getMetaDescription = ({ city, storeName, state }) => {
  const hasRequiredDescriptionData = !!city && !!state && !!storeName;
  const descriptionPostfix = `${storeName} store in ${city}, ${state}.`;

  if (!hasRequiredDescriptionData) return 'The Home Depot';

  return `See what shoppers are saying about their experience visiting The Home Depot ${descriptionPostfix}`;
};

const getMetadataValues = (data) => {
  const { emtData, storeData, seoPageNumber } = data;
  const emtContentMetadata = emtData?.emtContent?.content?.reviewsMetadata || {};
  const {
    city, storeName, postalCode, state, canonicalURL
  } = getStoreDetails({ data: storeData, path: `${LAST_PATH.REVIEWS}/${seoPageNumber}` });

  const title = emtContentMetadata?.title || getStoreReviewsTitle({
    city, storeName, postalCode, state, seoPageNumber
  });
  const metaDescription = emtContentMetadata?.metaDescription || getMetaDescription({ city, storeName, state });
  const ogTitle = emtContentMetadata?.ogTitle || getStoreReviewsTitle({
    city, storeName, postalCode, state, seoPageNumber
  });
  const ogDescription = emtContentMetadata?.ogDescription || getMetaDescription({ city, storeName, state });
  const ogType = emtContentMetadata?.ogType || 'PPS Review';
  const ogImage = emtContentMetadata?.ogImage || SD_OG_IMAGE;
  const schema = emtContentMetadata?.schema;

  return {
    canonicalURL,
    title,
    metaDescription,
    ogTitle,
    ogDescription,
    ogType,
    ogImage,
    schema,
  };
};

const storeReviewsHandler = ({ props }) => {
  const tags = [];

  const { seoPageNumber } = props;
  const totalReviews = props?.reviewData?.storeReviews?.TotalResults;
  const totalPages = Math.ceil(totalReviews / REVIEWS_PAGE_SIZE);

  const {
    title,
    canonicalURL,
    metaDescription,
    ogTitle,
    ogDescription,
    ogType,
    ogImage,
    schema,
  } = getMetadataValues(props);

  tags.push(new TitleTag(title));
  if (seoPageNumber > totalPages) tags.push(new MetaTag({ name: 'ROBOTS', content: 'NOINDEX', id: 'robots' }));
  tags.push(new MetaTag({ name: 'description', content: metaDescription, id: 'description' }));
  tags.push(new LinkTag({ rel: 'canonical', href: canonicalURL, id: 'canonical' }));
  tags.push(new MetaTag({ property: 'og:url', content: canonicalURL, id: 'canonicalURL' }));
  tags.push(new MetaTag({ property: 'og:type', content: ogType, id: 'ogType' }));
  tags.push(new MetaTag({ property: 'og:title', content: ogTitle, id: 'ogTitle' }));
  tags.push(new MetaTag({ property: 'og:description', content: ogDescription, id: 'ogDescription' }));
  tags.push(new MetaTag({ property: 'og:image', content: ogImage, id: 'ogImage' }));
  tags.push(new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'og-site_name' }));
  tags.push(new MetaTag({ property: 'fb:admins', content: OPEN_GRAPH_FACEBOOK_ADMIN, id: 'fb-admins' }));

  if (schema) {
    tags.push(new ScriptTag({ content: schema, id: 'storeReviewsStructuredData' }));
  } else {
    const breadcrumbSchema = getStructuredBreadcrumbs({
      props,
      departmentName: 'Reviews',
      departmentID: `${PAGE_TYPE.REVIEWS}/1`
    });
    const localBusinessSchema = getStoreDetailStructureData('LocalBusiness', props);

    tags.push(new ScriptTag({ content: breadcrumbSchema, id: 'storeReviewsStructuredBreadcrumb' }));
    tags.push(new ScriptTag({ content: localBusinessSchema, id: 'storeReviewsStructuredLocalBusinessData' }));
  }

  return tags;
};

const StoreReviewsMetadata = ({ storeId, seoPageNumber }) => {
  const options = {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    }
  };

  const emtContentOptions = {
    variables: {
      contentId: storeId,
      pageName: EMT_PAGE_NAMES.REVIEWS
    }
  };

  const reviewsOption = getGraphQlReviewsOptions({
    pageType: PAGE_TYPE.REVIEWS,
    pageSize: REVIEWS_PAGE_SIZE,
    storeId,
    page: seoPageNumber
  });

  const { data: storeData, loading: storeLoading, error: storeError } = useDataModel('storeSearch', options);
  const { data: emtData, loading: emtLoading, error: emtError } = useDataModel('emtContent', emtContentOptions);
  const { data: reviewData, loading: reviewLoading, error: reviewError } = useDataModel('storeReviews', reviewsOption);

  const props = {
    storeId, storeData, emtData, reviewData, seoPageNumber
  };

  useHelmet('storeReviewsMetadata', { props }, storeReviewsHandler, [storeId]);

  return null;
};

StoreReviewsMetadata.displayName = 'StoreReviewsMetadata';

StoreReviewsMetadata.dataModel = {
  storeSearch: params({
    storeSearchInput: string().isRequired(),
    pagesize: string().isRequired()
  }).shape({
    stores: arrayOf(shape({
      address: shape({
        city: string(),
        state: string(),
        postalCode: string(),
        street: string()
      }),
      storeId: string(),
      name: string(),
      phone: string(),
      storeDetailsPageLink: string(),
      storeHours: shape({
        sunday: shape({
          open: string(),
          close: string()
        }),
        monday: shape({
          open: string(),
          close: string()
        })
      })
    }))
  }),
  storeReviews: params({ storeId: string().isRequired() }).shape({
    Results: arrayOf(shape({
      Rating: number(),
      ReviewText: string(),
      Title: string(),
      UserNickname: string()
    })),
    Includes: shape({
      Products: shape({
        store: shape({
          FilteredReviewStatistics: shape({
            AverageOverallRating: string(),
            TotalReviewCount: number()
          })
        })
      })
    })
  }),
  emtContent: params({
    contentId: string().isRequired(),
    pageName: string().isRequired()
  }).shape({
    content: json()
  }),
};

StoreReviewsMetadata.propTypes = {
  storeId: PropTypes.string.isRequired,
  seoPageNumber: PropTypes.number.isRequired,
};

StoreReviewsMetadata.defaultProps = {};

export { StoreReviewsMetadata };